import { useEffect, useRef } from 'react';

const SESSION_COOKIE = 'meo_audit_session';

function getOrCreateSessionId(): string {
  const match = document.cookie.match(new RegExp(`(^| )${SESSION_COOKIE}=([^;]+)`));
  if (match) return match[2];
  // 16 random hex chars (~64 bits)
  const id = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  // 30-day cookie
  const exp = new Date();
  exp.setDate(exp.getDate() + 30);
  document.cookie = `${SESSION_COOKIE}=${id}; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
  return id;
}

/**
 * Logs a view event when the page is rendered, then sends the duration via
 * sendBeacon when the user navigates away.
 */
export function useViewTracking(token: string | undefined) {
  const sessionId = useRef<string>('');
  const startTime = useRef<number>(0);
  const sentInitial = useRef<boolean>(false);

  useEffect(() => {
    if (!token) return;
    sessionId.current = getOrCreateSessionId();
    startTime.current = Date.now();

    // Initial view event
    if (!sentInitial.current) {
      sentInitial.current = true;
      fetch(`/api/audit/${token}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, event: 'open' }),
        keepalive: true,
      }).catch(() => {});
    }

    const onLeave = () => {
      const seconds = Math.round((Date.now() - startTime.current) / 1000);
      const payload = JSON.stringify({
        session_id: sessionId.current,
        event: 'close',
        duration_seconds: seconds,
      });
      // sendBeacon is the reliable way to fire on unload.
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(`/api/audit/${token}/view`, blob);
      } else {
        fetch(`/api/audit/${token}/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', onLeave);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onLeave();
    });
    return () => {
      window.removeEventListener('beforeunload', onLeave);
    };
  }, [token]);
}
